<template>
  <div v-if="!label && (!crudManagerStore.isInitiated || !previewData || previewData.loading)">
    <q-skeleton
      bordered
      square
      :width="randomWitdh" />
  </div>
  <div v-else-if="textOnly">
    <div class="row items-center text-lowercase text-capitalize">
      <slot name="header" />
      <div class="text-weight-regular">
        <div v-if="prefix === true">{{ $t(`crudmodels.${modelName}.name`, 1) }}&nbsp;</div>
        <div v-if="typeof prefix === 'string'">{{ prefix }}&nbsp;</div>
      </div>
      <div
        v-if="!label && labelComponent"
        :class="`row text-${color}`">
        <component
          :is="labelComponent"
          :data="previewData.data" />
        {{ suffix }}
      </div>

      <div
        v-else
        :class="`row text-${color}`">
        {{ label ?? previewData.title }} {{ suffix }}
      </div>
    </div>
  </div>
  <div v-else>
    <q-btn
      v-if="btnOnly"
      :color="color"
      :size="size"
      :padding="padding"
      flat
      :icon="symRoundedVisibility"
      :aria-label="btnAriaLabel || $t('crudmanager.preview.open', { model: $t(`crudmodels.${modelName}.name`, 1) })"
      @click="handleClick">
      <q-menu ref="contextMenuRef">
        <q-card class="crud-preview-card">
          <q-linear-progress
            v-if="previewData.loading"
            indeterminate />
          <q-card-section class="q-py-sm">
            <div class="row justify-between items-center">
              <div class="column">
                <div>
                  <component
                    :is="headerComponent"
                    v-if="headerComponent"
                    :data="previewData.data" />
                  <template v-else>
                    <div class="text-h6">{{ previewData.title ?? previewData.data?.name }}</div>
                  </template>
                </div>
                <div class="text-caption text-weight-medium">{{ $t(`crudmodels.${modelName}.name`, 1) }}&nbsp;</div>
              </div>
            </div>
          </q-card-section>
          <q-separator />
          <q-card-section class="q-pa-sm">
            <component
              :is="mainComponent"
              v-if="previewData.data && mainComponent"
              :model-value="previewData.data" />
          </q-card-section>
          <q-card-section>
            <div class="row justify-between items-center q-gutter-sm">
              <component
                :is="actionsComponent"
                v-if="previewData.data && actionsComponent"
                :data="previewData.data" />
            </div>
            <div class="row justify-between">
              <div>
                <q-btn
                  dense
                  outline
                  color="primary"
                  :icon="symRoundedEdit"
                  @click="goToEditInstance(modelName, modelId)" />
              </div>
              <div>
                <q-btn
                  dense
                  outline
                  label="Fermer"
                  color="primary"
                  @click="closeMenu" />
              </div>
            </div>
          </q-card-section>
        </q-card>
      </q-menu>
    </q-btn>
    <q-btn
      v-else
      :color="color"
      :size="size"
      :padding="padding"
      flat
      @click="handleClick">
      <div class="row items-center text-lowercase text-capitalize">
        <slot name="header" />
        <div class="text-weight-regular">
          <div v-if="prefix === true">{{ $t(`crudmodels.${modelName}.name`, 1) }}&nbsp;</div>
          <div
            v-if="typeof prefix === 'string'"
            class="text-weight-medium">
            {{ prefix }}&nbsp;
          </div>
        </div>
        <div
          v-if="!label && labelComponent"
          class="row">
          <component
            :is="labelComponent"
            :data="previewData.data" />
          {{ suffix }}
        </div>

        <div v-else>{{ label ?? previewData.title }} {{ suffix }}</div>
      </div>
      <q-menu ref="contextMenuRef">
        <q-card class="crud-preview-card">
          <q-linear-progress
            v-if="previewData.loading"
            indeterminate />
          <q-card-section class="q-py-sm">
            <div class="row justify-between items-center">
              <div class="column">
                <div>
                  <component
                    :is="headerComponent"
                    v-if="headerComponent"
                    :data="previewData.data" />
                  <template v-else>
                    <div class="text-h6">{{ previewData.title ?? previewData.data?.name }}</div>
                  </template>
                </div>
                <div class="text-caption text-weight-medium">{{ $t(`crudmodels.${modelName}.name`, 1) }}&nbsp;</div>
              </div>
            </div>
          </q-card-section>
          <q-separator />
          <q-card-section class="q-pa-sm">
            <component
              :is="mainComponent"
              v-if="previewData.data && mainComponent"
              :model-value="previewData.data" />
          </q-card-section>
          <q-card-section>
            <div class="row justify-between items-center q-gutter-sm">
              <component
                :is="actionsComponent"
                v-if="previewData.data && actionsComponent"
                :data="previewData.data" />
            </div>
            <div class="row justify-between">
              <div>
                <q-btn
                  dense
                  outline
                  color="primary"
                  :icon="symRoundedEdit"
                  @click="goToEditInstance(modelName, modelId)" />
              </div>
              <div>
                <q-btn
                  dense
                  outline
                  label="Fermer"
                  color="primary"
                  @click="closeMenu" />
              </div>
            </div>
          </q-card-section>
        </q-card>
      </q-menu>
    </q-btn>
  </div>
</template>

<script setup lang="ts">
import { symRoundedEdit, symRoundedVisibility } from '@quasar/extras/material-symbols-rounded'
import { type QMenu } from 'quasar'
import useCrudUtils from 'src/CrudManager/Core/composables/CrudUtils/useCrudUtils'
import { useCrudManagerStore } from 'src/CrudManager/Core/stores/useCrudManagerStore'
import type { Component } from 'vue'
import { onMounted, onUnmounted, ref, watch } from 'vue'
import {
  fetchSinglePreviewData,
  initSocket,
  isPreviewStale,
  subscribeToPreview,
  type PreviewData,
} from './PreviewDB'

export interface Props {
  modelName: string
  modelId: string
  size?: string
  padding?: string
  textOnly?: boolean
  color?: string
  prefix?: string | boolean
  suffix?: string
  /** A supplied label stays visible independently of the live record (for historical snapshots). */
  label?: string
  /** Fetch the preview only when opened; list labels can come from their existing payload. */
  lazy?: boolean
  btnOnly?: boolean
  /** accessible name for the icon-only button — without it every peek button on a
   *  list reads identically to a screen reader */
  btnAriaLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  padding: '0px',
  textOnly: false,
  color: 'primary',
  prefix: false,
  suffix: '',
  lazy: false,
  btnOnly: false,
  btnAriaLabel: '',
})

const handleClick = (e: MouseEvent) => {
  e.stopPropagation()
  e.preventDefault()

  // Check if ctrl key is held down
  if (e.ctrlKey) {
    // Directly open edit mode
    goToEditInstance(props.modelName, props.modelId)
    return
  }

  void loadPreview()
}

const closeMenu = () => {
  contextMenuRef.value?.hide()
}
const { goToEditInstance, getModelConfig } = useCrudUtils()
const contextMenuRef = ref(null as QMenu | null)
const previewData = ref({
  id: props.modelId,
  model: props.modelName,
  data: null,
  loading: true,
  error: false,
  updatedAt: new Date(),
} as PreviewData)

const crudManagerStore = useCrudManagerStore()

const randomWitdh = `${Math.floor(Math.random() * 100) + 100}px`

const mainComponent = ref<Component | false | null>(null)

// Refactor these to refs
const labelComponent = ref<Component | false | null>(null)
const headerComponent = ref<Component | false | null>(null)
const actionsComponent = ref<Component | false | null>(null)

async function loadModelComponents() {
  if (crudManagerStore.isInitiated && props.modelName) {
    labelComponent.value = await crudManagerStore.getModelComponent(props.modelName, 'preview.label')
    headerComponent.value = await crudManagerStore.getModelComponent(props.modelName, 'preview.header')
    actionsComponent.value = await crudManagerStore.getModelComponent(props.modelName, 'preview.actions')
  }
}

watch(
  () => crudManagerStore.isInitiated,
  (initiated) => {
    if (initiated && (!props.lazy || unsubscribeFn)) void loadModelComponents()
  },
)
let unsubscribeFn: (() => void) | null = null

const subscribe = () => {
  unsubscribe()

  unsubscribeFn = subscribeToPreview(props.modelName, props.modelId, async (item) => {
    // If no data or stale data, trigger a fetch
    if (!item || !item.data || isPreviewStale(item.updatedAt)) {
      try {
        const fetched = await fetchSinglePreviewData(props.modelName, props.modelId)
        previewData.value = fetched
      } catch (error) {
        console.warn('Error fetching preview data:', error)
        previewData.value = {
          id: props.modelId,
          model: props.modelName,
          data: null,
          loading: false,
          error: true,
          title: '',
          updatedAt: new Date(),
        }
      }
    } else {
      previewData.value = item
    }
  })
}

const loadPreview = async () => {
  if (!props.modelId || unsubscribeFn) return
  subscribe()
  initSocket()
  const modelConfig = await getModelConfig(props.modelName)
  mainComponent.value = (await modelConfig?.lister?.cardComponent?.detailedComponent) || (await modelConfig?.lister?.cardComponent?.component)
  await loadModelComponents()
}

onMounted(() => {
  if (!props.lazy) void loadPreview()
})

watch(
  () => props.modelId,
  () => {
    unsubscribe()
    if (!props.lazy) void loadPreview()
  },
)

const unsubscribe = () => {
  if (unsubscribeFn) {
    unsubscribeFn()
    unsubscribeFn = null
  }
}

onUnmounted(() => {
  unsubscribe()
})
</script>

<style lang="scss">
.crud-preview-card {
  min-width: 360px;
  overflow-x: hidden;
}
</style>
